// frontend/js/socialEconomyHandlers.js
// Social Economy Integration for Trader Interface
import { apiBaseUrl } from './config.js';
import { getAuthToken } from './auth.js';
import { t, confirmT, updateElementText } from './translationHelper.js';
import translations from './translations.js';

if (!globalThis.ZTranslations) {
  globalThis.ZTranslations = translations;
}

if (typeof globalThis.$t !== 'function') {
  globalThis.$t = (key, params) => {
  return t(key);
  };
}

// === Utility Setup ===
const CACHE_DURATION = 10000; // 10 seconds
const portfolioCache = new Map();

// === Local, self-contained Z$ slider utilities (no imports needed) ===
async function fetchPortfolioZollars_LOCAL(apiBaseUrl, portfolioId, token) {
  const res = await fetch(`${apiBaseUrl}/api/trader/${portfolioId}/zollars`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!res.ok) throw new Error(`Failed to fetch zollars ${res.status}`);
  return res.json();
}

// === Shared "Zollars / Capital" holographic switch ===
function createModeSwitch(start = 'zollars') {
  const wrap = document.createElement('div');
  wrap.style.cssText = 'display:flex;gap:12px;justify-content:center;margin:12px 0 8px;';

  const make = (id, label) => {
    const b = document.createElement('button');
    b.id = id;
    b.textContent = label;
    b.style.cssText = `
      padding:10px 18px;border-radius:10px;font-family:'bithigh',monospace;
      cursor:pointer;transition:all .2s ease;background:rgba(0,0,0,.4);
      box-shadow:0 0 8px rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.3);
      color:#fcba03;
    `;
    b.dataset.active = '0';
    return b;
  };

  const zol = make('modeZollars','Zollars');
  const cap = make('modeCapital','Capital');

  const setActive = (btnOn) => {
    [zol, cap].forEach(b => {
      b.dataset.active = (b === btnOn) ? '1' : '0';
      if (b === btnOn) {
        b.style.background = 'rgba(252,186,3,.25)';
        b.style.boxShadow = '0 0 12px rgba(252,186,3,.8)';
        b.style.border = '1px solid #fcba03';
        b.style.color = '#fff';
      } else {
        b.style.background = 'rgba(0,0,0,.4)';
        b.style.boxShadow = '0 0 8px rgba(255,255,255,.08)';
        b.style.border = '1px solid rgba(255,255,255,.3)';
        b.style.color = '#fcba03';
      }
    });
  };

  wrap.appendChild(zol);
  wrap.appendChild(cap);
  setActive(start === 'zollars' ? zol : cap);

  return { el: wrap, zol, cap, setActive };
}


function aggregateZollarsByType_LOCAL(zollars) {
  const map = {
    erotic:'erotic','Erotic':'erotic',
    fellow:'fellowship','Fellow':'fellowship','fellowship':'fellowship','FellowShip':'fellowship',
    friend:'friendship','Friend':'friendship','friendship':'friendship','Friendship':'friendship','FriendShip':'friendship',
    hate:'hate','Hate':'hate',
    love:'love','Love':'love',
    superficial:'superficial','SuperFicial':'superficial','Superficial':'superficial',
    superficiality:'superficial','Superficiality':'superficial',
    toxic:'toxic','Toxic':'toxic',
    death:'death','Death':'death',
    'long life':'long life','Long Life':'long life','longlife':'long life'
  };
  const totals = {};
  console.log(`📊 [aggregateZollars] Processing ${(zollars||[]).length} zollars`);
  (zollars||[]).forEach(z => {
    // Use regex to handle case-insensitive "Zollar" removal (matches backend logic)
    let t = String(z.zollar_name||'').replace(/\s*[Zz]ollar\s*$/i, '').trim().toLowerCase();
    // Check map for normalized version
    t = map[t] || t;
    if (!t) return;
    totals[t] = (totals[t]||0) + 1;
    console.log(`  "${z.zollar_name}" → "${t}" (total: ${totals[t]})`);
  });
  console.log(`📊 [aggregateZollars] Final totals:`, totals);
  return totals;
}

function qualitativeFromFraction_LOCAL(r){
  if (r <= 0) return 'None';
  if (r <= 0.03)  return 'Barely Any';
  if (r <= 0.07)  return 'A Trace';
  if (r <= 0.12)  return 'A Hint';
  if (r <= 0.18)  return 'A Little';
  if (r <= 0.26)  return 'A Few';
  if (r <= 0.33)  return 'Some';
  if (r <= 0.40)  return 'Several';
  if (r <= 0.50)  return 'Moderate';
  if (r <= 0.58)  return 'Noticeable';
  if (r <= 0.65)  return 'Considerable';
  if (r <= 0.72)  return 'Many';
  if (r <= 0.78)  return 'A Lot';
  if (r <= 0.83)  return 'Abundant';
  if (r <= 0.88)  return 'Heaps';
  if (r <= 0.92)  return 'A Great Deal';
  if (r <= 0.95)  return 'Almost All';
  if (r <= 0.97)  return 'Nearly Everything';
  if (r <= 0.985) return 'Overflowing';
  if (r <= 0.995) return 'Virtually Everything';
  return 'All You Have';
}

function colorForFraction_LOCAL(p) {
  const r = Math.max(0, Math.min(1, p));
  if (r <= 0.02)  return 'rgba(255, 255, 153, 0.35)';
  if (r <= 0.05)  return 'rgba(255, 247, 130, 0.4)';
  if (r <= 0.08)  return 'rgba(255, 239, 100, 0.45)';
  if (r <= 0.12)  return 'rgba(255, 230, 80, 0.5)';
  if (r <= 0.16)  return 'rgba(255, 216, 60, 0.55)';
  if (r <= 0.20)  return 'rgba(255, 204, 40, 0.6)';
  if (r <= 0.26)  return 'rgba(255, 190, 30, 0.65)';
  if (r <= 0.32)  return 'rgba(255, 175, 20, 0.68)';
  if (r <= 0.38)  return 'rgba(255, 160, 10, 0.7)';
  if (r <= 0.44)  return 'rgba(255, 145, 5, 0.72)';
  if (r <= 0.50)  return 'rgba(255, 130, 0, 0.74)';
  if (r <= 0.58)  return 'rgba(255, 115, 0, 0.76)';
  if (r <= 0.66)  return 'rgba(255, 100, 0, 0.78)';
  if (r <= 0.74)  return 'rgba(255, 85, 0, 0.75)';
  if (r <= 0.82)  return 'rgba(255, 70, 10, 0.75)';
  if (r <= 0.88)  return 'rgba(255, 55, 20, 0.75)';
  if (r <= 0.93)  return 'rgba(255, 40, 25, 0.75)';
  if (r <= 0.97)  return 'rgba(255, 25, 30, 0.75)';
  if (r <= 0.99)  return 'rgba(240, 15, 40, 0.75)';
  return 'rgba(220, 5, 30, 0.88)';
}

/**
 * Create abstract sliders for borrowing (no specific quantities)
 */
function createAbstractBorrowSliders_LOCAL() {
  const container = document.createElement('div');
  container.style.cssText = 'display:flex;flex-direction:column;gap:20px;padding:12px 0;';

  let zollarValue = 0;
  let capitalValue = 0;

  // Zollar Slider
  const zollarSection = document.createElement('div');
  zollarSection.innerHTML = `
    <label style="display:block;margin-bottom:8px;font-family:'bithigh',monospace;color:#fcba03;font-size:1.1em;">
      Zollars
    </label>
    <input type="range" class="abstract-zollar-slider range-slider" min="0" max="100" value="0" style="width:100%;height:10px;background:rgba(252,186,3,0.2);border-radius:4px;outline:none;cursor:pointer;-webkit-appearance:none;appearance:none;">
    <div style="display:flex;justify-content:space-between;margin-top:4px;font-family:'bithigh',monospace;font-size:0.85em;color:#999;">
      <span>None</span>
      <span class="zollar-magnitude" style="color:#fcba03;">None</span>
      <span>Maximum</span>
    </div>
  `;

  const zollarSlider = zollarSection.querySelector('.abstract-zollar-slider');
  const zollarMagnitude = zollarSection.querySelector('.zollar-magnitude');

  zollarSlider.addEventListener('input', () => {
    zollarValue = parseInt(zollarSlider.value) || 0;
    const frac = zollarValue / 100;
    zollarMagnitude.textContent = qualitativeFromFraction_LOCAL(frac);
    zollarSlider.style.background = `linear-gradient(to right, rgba(252,186,3,0.6) 0%, rgba(252,186,3,0.6) ${zollarValue}%, rgba(252,186,3,0.2) ${zollarValue}%, rgba(252,186,3,0.2) 100%)`;
  });

  // Capital Slider
  const capitalSection = document.createElement('div');
  capitalSection.innerHTML = `
    <label style="display:block;margin-bottom:8px;font-family:'bithigh',monospace;color:#fcba03;font-size:1.1em;">
      Capital
    </label>
    <input type="range" class="abstract-capital-slider range-slider" min="0" max="100" value="0" style="width:100%;height:10px;background:rgba(252,186,3,0.2);border-radius:4px;outline:none;cursor:pointer;-webkit-appearance:none;appearance:none;">
    <div style="display:flex;justify-content:space-between;margin-top:4px;font-family:'bithigh',monospace;font-size:0.85em;color:#999;">
      <span>None</span>
      <span class="capital-magnitude" style="color:#fcba03;">None</span>
      <span>Maximum</span>
    </div>
  `;

  const capitalSlider = capitalSection.querySelector('.abstract-capital-slider');
  const capitalMagnitude = capitalSection.querySelector('.capital-magnitude');

  capitalSlider.addEventListener('input', () => {
    capitalValue = parseInt(capitalSlider.value) || 0;
    const frac = capitalValue / 100;
    capitalMagnitude.textContent = qualitativeFromFraction_LOCAL(frac);
    capitalSlider.style.background = `linear-gradient(to right, rgba(252,186,3,0.6) 0%, rgba(252,186,3,0.6) ${capitalValue}%, rgba(252,186,3,0.2) ${capitalValue}%, rgba(252,186,3,0.2) 100%)`;
  });

  container.appendChild(zollarSection);
  container.appendChild(capitalSection);

  return {
    el: container,
    getZollarMagnitude: () => zollarValue,
    getCapitalMagnitude: () => capitalValue,
    hasSelection: () => zollarValue > 0 || capitalValue > 0
  };
}

function createLocalZollarSliders_LOCAL(relationshipTotals) {
  const container = document.createElement('div');
  container.style.cssText = 'display:flex;flex-direction:column;gap:14px;padding:6px 0;';

  const bid = {};
  const types = Object.keys(relationshipTotals||{}).filter(k=>relationshipTotals[k]>0);

  const cap = s => s.charAt(0).toUpperCase()+s.slice(1);

  types.forEach(type => {
    const max = Number(relationshipTotals[type])||0;
    bid[type] = 0;

    const row = document.createElement('div');
    row.style.cssText = 'display:flex;flex-direction:column;gap:6px;';

    const head = document.createElement('div');
    head.style.cssText = 'display:flex;justify-content:space-between;align-items:center;';
    head.innerHTML = `
      <span style="font-family:'bithigh',monospace;color:#fcba03;">${cap(type)}</span>
      <span id="desc-${type}" style="font-family:'bithigh',monospace;color:#fcba03;opacity:.9;">None</span>
    `;

    const bar = document.createElement('div');
    bar.style.cssText = `
      position:relative;width:100%;height:40px;background:rgba(0,0,0,.3);
      border:2px solid #fcba03;border-radius:8px;overflow:hidden;cursor:pointer;box-shadow:0 0 10px rgba(252,186,3,.5);
    `;
    const base = document.createElement('div');
    base.style.cssText = 'position:absolute;inset:0;background:#fcba03;opacity:.15;';
    const fill = document.createElement('div');
    fill.id = `fill-${type}`;
    fill.style.cssText = `
      position:absolute;top:0;left:0;height:100%;width:0%;
      background:rgba(252,186,3,.4);box-shadow:0 0 12px rgba(252,186,3,.7);
      transition:width .15s ease, background-color .15s ease;
    `;
    bar.appendChild(base); bar.appendChild(fill);

    const hidden = document.createElement('input');
    hidden.type = 'range'; hidden.min = '0'; hidden.max = String(max); hidden.value = '0'; hidden.style.display='none';
    hidden.id = `slider-${type}`;

    const updateAt = clientX => {
      const rect = bar.getBoundingClientRect();
      const p = Math.max(0, Math.min(1, (clientX-rect.left)/rect.width));
      const raw = Math.round(p * max);
      hidden.value = String(raw);
      bid[type] = raw;
      const frac = max>0 ? raw/max : 0;
      fill.style.width = `${frac*100}%`;
      const c = colorForFraction_LOCAL(frac);
      fill.style.backgroundColor = c;
      fill.style.boxShadow = `0 0 15px ${c}`;
      const d = document.getElementById(`desc-${type}`);
      if (d) d.textContent = qualitativeFromFraction_LOCAL(frac);
    };

    let dragging = false;
    bar.addEventListener('mousedown', e => { dragging = true; updateAt(e.clientX); });
    document.addEventListener('mousemove', e => { if (dragging) updateAt(e.clientX); });
    document.addEventListener('mouseup', () => dragging=false);
    bar.addEventListener('click', e => updateAt(e.clientX));
    bar.addEventListener('touchstart', e => { dragging=true; updateAt(e.touches[0].clientX); e.preventDefault(); });
    document.addEventListener('touchmove', e => { if (dragging) updateAt(e.touches[0].clientX); });
    document.addEventListener('touchend', () => dragging=false);

    row.appendChild(head);
    row.appendChild(bar);
    row.appendChild(hidden);
    container.appendChild(row);
  });

  return {
    el: container,
    getBid: () => ({ ...bid }),
    total: () => Object.values(bid).reduce((a,b)=>a+(b||0),0),
    validate: () => true // holdings are baked into max; can’t exceed
  };
}

function renderModeSwitch_LOCAL(defaultMode='capital') {
  const wrap = document.createElement('div');
  wrap.style.cssText = 'display:flex;gap:10px;margin:10px 0 6px;';
  const mk = (id, label, active=false) => {
    const b = document.createElement('button');
    b.id = id; b.textContent = label;
    b.className = active ? 'mode-btn active':'mode-btn';
    b.style.cssText = `
      flex:1;padding:10px 12px;border:1px solid #fcba03;background:rgba(0,0,0,.35);
      color:#fcba03;font-family:'bithigh',monospace;cursor:pointer;border-radius:8px;
      box-shadow:${active?'0 0 14px rgba(252,186,3,.8) inset':'0 0 6px rgba(252,186,3,.4) inset'};
    `;
    return b;
  };
  const cap = mk('modeCapital','Capital', defaultMode==='capital');
  const zol = mk('modeZollars','Zollars', defaultMode==='zollars');
  wrap.appendChild(cap); wrap.appendChild(zol);
  return wrap;
}

// === Portfolio Preload + Cache System ===
async function preloadPortfolios() {
  const sessionId = localStorage.getItem('sessionId');
  if (!sessionId) return;

  try {
    const [active, richest] = await Promise.all([
      fetch(`${apiBaseUrl}/api/portfolios/active`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      }).then((r) => r.json()),
      fetch(`${apiBaseUrl}/api/portfolios/richest`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      }).then((r) => r.json()),
    ]);

    const now = Date.now();
    portfolioCache.set('active', { data: active, timestamp: now });
    portfolioCache.set('richest', { data: richest, timestamp: now });
    console.log('📦 Preloaded active and richest portfolios');
  } catch (error) {
    console.error('❌ Failed to preload portfolios:', error);
  }
}

async function getCachedPortfolios(sessionId, endpoint = '/api/portfolios/active') {
  const cacheKey = endpoint.includes('richest') ? 'richest' : 'active';
  const cached = portfolioCache.get(cacheKey);

  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }

  try {
    const response = await fetch(`${apiBaseUrl}${endpoint}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sessionId }),
    });
    const data = await response.json();
    portfolioCache.set(cacheKey, { data, timestamp: Date.now() });
    return data;
  } catch (error) {
    console.error('❌ Portfolio fetch failed:', error);
    return { portfolios: [] };
  }
}

// === Emotional Debt Meter Initialization ===
document.addEventListener('DOMContentLoaded', () => {
  const portfolioId = localStorage.getItem('portfolioId');
  if (portfolioId && typeof EmotionalDebtMeter !== 'undefined') {
    const emotionalMeter = new EmotionalDebtMeter('emotional-debt-meter', portfolioId);
    emotionalMeter.init();
    console.log('💞 Emotional Debt Meter initialized');
    window.emotionalMeter = emotionalMeter;
  }

  preloadPortfolios(); // Preload early
});

// === Social Economy Modal Manager ===
class SocialEconomyModal {
  constructor() {
    this.activeModal = null;
  }

  async fetchAllActivePortfolios() {
    const portfolioId = localStorage.getItem('portfolioId');
    const sessionId = localStorage.getItem('sessionId');
    if (!sessionId) {
      console.warn('⚠️ No session found for portfolio fetch');
      return { portfolios: [], currentPortfolioId: portfolioId, hasSession: false };
    }

    try {
      const data = await getCachedPortfolios(sessionId, '/api/portfolios/active');
      const portfolios = data.portfolios || data.data?.portfolios || [];
      return { portfolios, currentPortfolioId: portfolioId, hasSession: true };
    } catch (error) {
      console.error('❌ [SocialEconomy] Failed to fetch portfolios:', error);
      return { portfolios: [], currentPortfolioId: portfolioId, hasSession: false };
    }
  }

  createModal(title, content, onConfirm, onCancel) {
    this.closeModal();

    const overlay = document.createElement('div');
    overlay.className = 'social-economy-popup-overlay active';

    const modal = document.createElement('div');
    modal.className = 'social-economy-popup-content';

    const titleElement = document.createElement('h2');
    titleElement.textContent = title;
    modal.appendChild(titleElement);

    if (typeof content === 'string') modal.innerHTML += content;
    else modal.appendChild(content);

    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'social-economy-popup-buttons';

    const confirmBtn = document.createElement('button');
    confirmBtn.className = 'confirm-btn';
    confirmBtn.textContent = 'Confirm';
    confirmBtn.onclick = () => onConfirm?.(modal);

    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'cancel-btn';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.onclick = () => {
      this.closeModal();
      onCancel?.();
    };

    buttonContainer.appendChild(confirmBtn);
    buttonContainer.appendChild(cancelBtn);
    modal.appendChild(buttonContainer);

    overlay.appendChild(modal);
    document.body.appendChild(overlay);
    this.activeModal = overlay;

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        this.closeModal();
        onCancel?.();
      }
    });
  }

  closeModal() {
    if (this.activeModal) {
      this.activeModal.remove();
      this.activeModal = null;
      // Always restore body scroll when closing modal
      document.body.style.overflow = '';
    }
  }

  showMessage(message, isSuccess = true, autoCloseMs = 5000, keepModalOpen = false) {
    // Close any existing modal first UNLESS keepModalOpen is true
    if (!keepModalOpen) {
      this.closeModal();
    }

    // Create overlay with forced visibility
    const overlay = document.createElement('div');
    overlay.className = 'social-economy-popup-overlay active';
    overlay.dataset.message = message;
    // Force display immediately to prevent CSS timing issues
    overlay.style.display = 'flex';
    overlay.style.position = 'fixed';
    overlay.style.inset = '0';
    overlay.style.zIndex = '10000'; // Higher than modal (9999) so it appears on top
    overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.85)';
    overlay.style.justifyContent = 'center';
    overlay.style.alignItems = 'center';

    const modal = document.createElement('div');
    modal.className = 'social-economy-popup-content';

    const titleElement = document.createElement('h2');
    titleElement.textContent = isSuccess ? 'Success' : 'Error';
    modal.appendChild(titleElement);

    const content = document.createElement('div');
    content.style.cssText = 'text-align: center; padding: 20px;';
    content.innerHTML = `<p style="font-size: 16px; color: ${isSuccess ? 'rgba(61, 210, 2, 0.9)' : '#ff4444'};">${message}</p>`;
    modal.appendChild(content);

    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'social-economy-popup-buttons';

    const closeBtn = document.createElement('button');
    closeBtn.className = 'cancel-btn';
    closeBtn.textContent = 'Close';
    closeBtn.onclick = (e) => {
      e.stopPropagation();
      e.preventDefault();
      overlay.remove();
    };

    buttonContainer.appendChild(closeBtn);
    modal.appendChild(buttonContainer);

    overlay.appendChild(modal);

    // Ensure DOM is ready and append
    requestAnimationFrame(() => {
      document.body.appendChild(overlay);

      // Force a reflow to ensure rendering
      overlay.offsetHeight;
    });

    // Auto-close after specified time (default 5000ms = 5 seconds)
    // Pass null or 0 to disable auto-close for important messages
    if (autoCloseMs !== null && autoCloseMs > 0) {
      setTimeout(() => overlay.remove(), autoCloseMs);
    }

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) overlay.remove();
    });
  }

  createPortfolioSelection(portfolios, label) {
    const container = document.createElement('div');
    const labelElement = document.createElement('label');
    labelElement.textContent = label;
    container.appendChild(labelElement);

    const currentId = localStorage.getItem('portfolioId');
    const filtered = portfolios.filter((p) => p._id !== currentId);

    const select = document.createElement('select');
    select.className = 'portfolio-selector';

    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = '-- Select --';
    select.appendChild(defaultOption);

filtered.forEach((p) => {
      const option = document.createElement('option');
      option.value = p._id;
      option.dataset.isBot = p.isBot ? 'true' : 'false';
      option.textContent = `${p.name} (${p.wealth || 0})`;
      select.appendChild(option);
    });


    container.appendChild(select);
    return container;
  }

  getSelectedPortfolio(modal) {
    const selector = modal.querySelector('.portfolio-selector');
    if (!selector) return null;
    return selector.tagName === 'SELECT'
      ? { type: 'id', value: selector.value }
      : { type: 'name', value: selector.value.trim() };
  }
}

const modalManager = new SocialEconomyModal();
window.modalManager = modalManager;

// === Action Handlers ===
document.addEventListener('DOMContentLoaded', () => {
  const token = getAuthToken();

  // ✅ Helper function for authorized POST
  async function authorizedPost(url, payload) {
    return fetch(`${apiBaseUrl}${url}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }).then((r) => r.json());
  }

// === Help Friend (Z$ default, toggle to Capital) ===
  const helpFriendButton = document.getElementById('helpFriendButton');
  if (helpFriendButton) {
    helpFriendButton.addEventListener('click', async () => {
      const portfolioId = localStorage.getItem('portfolioId');
      const sessionId   = localStorage.getItem('sessionId');
      const token       = localStorage.getItem('token');
      if (!portfolioId || !sessionId || !token) {
        return modalManager.showMessage(t('debt_actions.common.error_login_required', 'Please log in first'), false);
      }
  
      const res = await fetch(`${apiBaseUrl}/api/portfolios/active`, {
        method:'POST',
        headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
        body: JSON.stringify({ sessionId })
      });
      const data = await res.json();
      const portfolios = Array.isArray(data) ? data : (data.portfolios||[]);
  
      const contentDiv = document.createElement('div');
      contentDiv.innerHTML = `<p>${t('debt_actions.help_friend.intro', 'Give help. Choose <b>Z$</b> or <b>Capital</b> for the gift.')}</p>`;

      // holographic perceived label
      const badgeRow = document.createElement('div');
      badgeRow.style.cssText='display:flex;justify-content:flex-end;margin:6px 0 4px;';
      badgeRow.innerHTML = `<span id="help-perceived" style="
        font-family:'bithigh',monospace;color:#fcba03;border:1px solid #fcba03;
        padding:6px 10px;border-radius:8px;background:rgba(0,0,0,.35);
        box-shadow:0 0 6px rgba(252,186,3,.4) inset;">${t('debt_actions.common.perceived_magnitude', 'Perceived magnitude')}: ${t('debt_actions.common.magnitude_none', 'None')}</span>`;
      contentDiv.appendChild(badgeRow);
      const setBadge = (txt) => contentDiv.querySelector('#help-perceived').textContent = `${t('debt_actions.common.perceived_magnitude', 'Perceived magnitude')}: ${txt}`;
  
      // toggle (default zollars)
      const sw = createModeSwitch('zollars');
      contentDiv.appendChild(sw.el);
  
      // capital UI
      const capWrap = document.createElement('div');
      capWrap.style.display='none';
      capWrap.innerHTML = `
        <label>${t('debt_actions.help_friend.capital_label', 'Capital (amount)')}</label>
        <input type="number" class="help-capital" min="1" value="50" placeholder="${t('debt_actions.help_friend.capital_placeholder', 'Enter capital')}">`;
      contentDiv.appendChild(capWrap);

      // zollars UI
      const zWrap = document.createElement('div');
      zWrap.id='help-zollars-ui';
      zWrap.innerHTML = `<p style="margin:8px 0 0;">${t('debt_actions.help_friend.select_zollars', 'Select Z$ categories to gift.')}</p>`;
      contentDiv.appendChild(zWrap);
  
      // recipient
      const sel = modalManager.createPortfolioSelection(portfolios, t('debt_actions.help_friend.recipient_label', 'Recipient:'));
      contentDiv.appendChild(sel);
  
      // helpers
      const clamp01 = v => Math.max(0, Math.min(1, v));
      const perceivedLabelFrom01 = v => qualitativeFromFraction_LOCAL(clamp01(v));
      const affordabilityProxy01 = (amount, wealthNow=Number(localStorage.getItem('tradingPoints')||0), near=0)=>{
        const z = Number(wealthNow)||0, need=Number(near)||0;
        const buffer = Math.max(1, z-need);
        return clamp01(Math.log1p(Number(amount)||0) / Math.log1p(buffer+(Number(amount)||0)));
      };
  
      // mount Z$ sliders immediately (default)
      try {
        const zData = await fetchPortfolioZollars_LOCAL(apiBaseUrl, portfolioId, token);
        const totals= aggregateZollarsByType_LOCAL(zData);
        zWrap._ZTOTAL = Object.values(totals).reduce((a,b)=>a+(b||0),0);
        const comp  = createLocalZollarSliders_LOCAL(totals);
        const slot  = document.createElement('div'); slot.style.marginTop='8px'; slot.appendChild(comp.el);
        zWrap.appendChild(slot); zWrap._ZCOMP = comp;
        const recomputeZ = () => {
          const frac = zWrap._ZTOTAL>0 ? comp.total()/zWrap._ZTOTAL : 0;
          setBadge(perceivedLabelFrom01(frac));
        };
        slot.addEventListener('mouseup',recomputeZ);
        slot.addEventListener('touchend',recomputeZ,{passive:true});
        slot.addEventListener('click',recomputeZ);
        setBadge(t('debt_actions.common.magnitude_hint', 'A Hint')); // gentle default
      } catch(e) {
        console.error('Z$ load failed:', e);
        zWrap.innerHTML = `<p style="color:#ff3333;">${t('debt_actions.help_friend.no_zollars', 'No Z$ available.')}</p>`;
        setBadge(t('debt_actions.common.magnitude_none', 'None'));
      }
  
      const toCapital=()=>{
        sw.setActive(sw.cap);
        zWrap.style.display='none';
        capWrap.style.display='';
        const amt = parseFloat(contentDiv.querySelector('.help-capital')?.value)||0;
        setBadge(perceivedLabelFrom01(affordabilityProxy01(amt)));
      };
      const toZollars=()=>{
        sw.setActive(sw.zol);
        capWrap.style.display='none';
        zWrap.style.display='';
        const total = zWrap._ZCOMP ? zWrap._ZCOMP.total() : 0;
        const frac = zWrap._ZTOTAL>0 ? total/zWrap._ZTOTAL : 0;
        setBadge(perceivedLabelFrom01(frac));
      };
      sw.cap.onclick = toCapital;
      sw.zol.onclick = toZollars;
  
      // live update for capital
      contentDiv.addEventListener('input', (e)=>{
        if (e.target && e.target.classList.contains('help-capital')) {
          const amt = parseFloat(e.target.value)||0;
          setBadge(perceivedLabelFrom01(affordabilityProxy01(amt)));
        }
      });
  
      modalManager.createModal(t('debt_actions.help_friend.modal_title', 'Altrau Z Bill (Altruistic Gift)'), contentDiv, async (modal)=>{
        const recipient = modalManager.getSelectedPortfolio(modal);
        if (!recipient || !recipient.value) return modalManager.showMessage(t('debt_actions.help_friend.error_no_recipient', 'Select a recipient.'), false, 3000, true);

        let payload;
        if (capWrap.style.display === '') {
          const points = parseFloat(contentDiv.querySelector('.help-capital').value)||0;
          if (points<=0) return modalManager.showMessage(t('debt_actions.help_friend.error_invalid_capital', 'Enter a valid capital amount.'), false, 3000, true);
          payload = { helperId: portfolioId, targetId: recipient.value, amount: points, package:{ points, zollars:0, stocks:0 }, sessionId, type:'help' };
        } else {
          if (!zWrap._ZCOMP) return modalManager.showMessage(t('debt_actions.help_friend.error_no_zollars_to_give', 'No Z$ to give.'), false, 3000, true);
          const zBid = zWrap._ZCOMP.getBid(), totalZ=zWrap._ZCOMP.total();
          if (totalZ<=0) return modalManager.showMessage(t('debt_actions.help_friend.error_select_zollars', 'Select some Z$ to give.'), false, 3000, true);

          // Validate each zollar type against available holdings
          const availableTotals = aggregateZollarsByType_LOCAL(await fetchPortfolioZollars_LOCAL(apiBaseUrl, portfolioId, token));
          for (const [type, count] of Object.entries(zBid)) {
            if (count > (availableTotals[type] || 0)) {
              return modalManager.showMessage(
                t('debt_actions.help_friend.error_insufficient_zollars', 'Insufficient {type} zollars. You have {available}, trying to send {count}.')
                  .replace('{type}', type)
                  .replace('{available}', availableTotals[type] || 0)
                  .replace('{count}', count),
                false,
                3000,
                true
              );
            }
          }

          payload = { helperId: portfolioId, targetId: recipient.value, amount: totalZ, package:{ points:0, zollars:zBid, stocks:0 }, sessionId, type:'help' };
        }

        modalManager.closeModal();
        await fetch(`${apiBaseUrl}/api/social-economy/help`, {
          method:'POST', headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' }, body: JSON.stringify(payload)
        });
        modalManager.showMessage(t('debt_actions.help_friend.success', 'Altrau Z Bill given. Your ledgers are now bound.'), true);
        if (window.emotionalMeter) window.emotionalMeter.fetchData();
      });
    });
  }


// === Predatory Lend (CDS) — Z$ default, toggle to Capital, calls /cds ===
  const predatoryLendButton = document.getElementById('predatoryLendButton');
  if (predatoryLendButton) {
    predatoryLendButton.addEventListener('click', async () => {
      const portfolioId = localStorage.getItem('portfolioId');
      const sessionId   = localStorage.getItem('sessionId');
      const token       = localStorage.getItem('token');
      if (!portfolioId || !sessionId || !token)
        return modalManager.showMessage(t('debt_actions.common.error_login_required', 'Please log in first'), false);
  
      const res = await fetch(`${apiBaseUrl}/api/portfolios/active`, {
        method:'POST',
        headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
        body: JSON.stringify({ sessionId })
      });
      const data = await res.json();
      const portfolios = Array.isArray(data) ? data : (data.portfolios||[]);
  
      const contentDiv = document.createElement('div');
      contentDiv.innerHTML = `
        <p>${t('debt_actions.predatory_lend.intro', 'Z-Payday CDO: Offer a compounding loan that reloads every 60 seconds.')}</p>
        <p style="color:#ff4d4d;font-size:.9em;">${t('debt_actions.predatory_lend.warning', 'WARNING: Each cycle increases the debt by 15% until repayment or bankruptcy.')}</p>
        <p style="color:#fcba03;font-size:.85em;font-style:italic;">${t('debt_actions.predatory_lend.exploitation_note', '(Exploitation disguised as empathic connection.)')}</p>
      `;

      // Add "Target Victim" vs "List on Market" choice
      const targetChoice = document.createElement('div');
      targetChoice.style.cssText = 'margin:12px 0;display:flex;gap:10px;';
      targetChoice.innerHTML = `
        <button class="mode-btn active" id="targetVictimBtn" style="flex:1;padding:10px;background:rgba(255,77,77,.25);border:1px solid #ff4d4d;color:#fff;font-family:'bithigh',monospace;cursor:pointer;border-radius:8px;">Target Specific Victim</button>
        <button class="mode-btn" id="listCDOOnMarketBtn" style="flex:1;padding:10px;background:rgba(0,0,0,.35);border:1px solid rgba(255,255,255,.3);color:#ff4d4d;font-family:'bithigh',monospace;cursor:pointer;border-radius:8px;">List on Market</button>
      `;
      contentDiv.appendChild(targetChoice);

      // perceived label (red-themed)
      const badgeRow = document.createElement('div');
      badgeRow.style.cssText='display:flex;justify-content:flex-end;margin:6px 0 4px;';
      badgeRow.innerHTML = `<span id="cds-perceived" style="
        font-family:'bithigh',monospace;color:#ff4d4d;border:1px solid #ff4d4d;
        padding:6px 10px;border-radius:8px;background:rgba(0,0,0,.35);
        box-shadow:0 0 6px rgba(255,77,77,.4) inset;">${t('debt_actions.common.perceived_magnitude', 'Perceived magnitude')}: ${t('debt_actions.common.magnitude_hint', 'A Hint')}</span>`;
      contentDiv.appendChild(badgeRow);
      const setBadge = (txt)=> contentDiv.querySelector('#cds-perceived').textContent = `${t('debt_actions.common.perceived_magnitude', 'Perceived magnitude')}: ${txt}`;
  
      // toggle (default zollars)
      const sw = createModeSwitch('zollars');
      contentDiv.appendChild(sw.el);
  
      // capital UI
      const capWrap = document.createElement('div');
      capWrap.style.display = 'none';
      capWrap.innerHTML = `
        <label>${t('debt_actions.predatory_lend.capital_label', 'Capital (amount to "lend")')}</label>
        <input type="number" class="cds-capital" min="10" value="100" placeholder="${t('debt_actions.predatory_lend.capital_placeholder', 'Amount')}">`;
      contentDiv.appendChild(capWrap);

      // zollars UI
      const zWrap = document.createElement('div');
      zWrap.id='cds-zollars-ui';
      zWrap.innerHTML = `<p style="margin:8px 0 0;">${t('debt_actions.predatory_lend.select_zollars', 'Select Z$ bait for collapse.')}</p>`;
      contentDiv.appendChild(zWrap);

      // Victim selection (only shown when targeting specific victim)
      const victimSelectContainer = document.createElement('div');
      victimSelectContainer.id = 'victimSelectContainer';
      const victimSelect = modalManager.createPortfolioSelection(portfolios, t('debt_actions.predatory_lend.victim_label', 'Target Victim:'));
      victimSelectContainer.appendChild(victimSelect);
      contentDiv.appendChild(victimSelectContainer);

      // Toggle between target victim and list on market
      let isMarketListing = false;
      const targetVictimBtn = targetChoice.querySelector('#targetVictimBtn');
      const listCDOOnMarketBtn = targetChoice.querySelector('#listCDOOnMarketBtn');

      const showVictimSelect = () => {
        isMarketListing = false;
        victimSelectContainer.style.display = '';
        targetVictimBtn.style.background = 'rgba(255,77,77,.25)';
        targetVictimBtn.style.color = '#fff';
        listCDOOnMarketBtn.style.background = 'rgba(0,0,0,.35)';
        listCDOOnMarketBtn.style.color = '#ff4d4d';
      };

      const showMarketListing = () => {
        isMarketListing = true;
        victimSelectContainer.style.display = 'none';
        listCDOOnMarketBtn.style.background = 'rgba(255,77,77,.25)';
        listCDOOnMarketBtn.style.color = '#fff';
        targetVictimBtn.style.background = 'rgba(0,0,0,.35)';
        targetVictimBtn.style.color = '#ff4d4d';
      };

      targetVictimBtn.onclick = showVictimSelect;
      listCDOOnMarketBtn.onclick = showMarketListing;
  
      // helpers
      const clamp01 = v => Math.max(0, Math.min(1, v));
      const perceivedLabelFrom01 = v => qualitativeFromFraction_LOCAL(clamp01(v));
      const affordabilityProxy01 = (amount, wealthNow=Number(localStorage.getItem('tradingPoints')||0), near=0)=>{
        const z = Number(wealthNow)||0, need=Number(near)||0; const buffer=Math.max(1,z-need);
        return clamp01(Math.log1p(Number(amount)||0) / Math.log1p(buffer+(Number(amount)||0)));
      };
  
      // mount zollar sliders immediately (default)
      try {
        const zData = await fetchPortfolioZollars_LOCAL(apiBaseUrl, portfolioId, token);
        const totals= aggregateZollarsByType_LOCAL(zData);
        zWrap._ZTOTAL = Object.values(totals).reduce((a,b)=>a+(b||0),0);
        const comp  = createLocalZollarSliders_LOCAL(totals);
        const slot  = document.createElement('div'); slot.style.marginTop='8px'; slot.appendChild(comp.el);
        zWrap.appendChild(slot); zWrap._ZCOMP = comp;
        const recomputeZ = () => {
          const frac = zWrap._ZTOTAL>0 ? comp.total()/zWrap._ZTOTAL : 0;
          setBadge(perceivedLabelFrom01(Math.min(1, frac*1.15))); // CDS bias
        };
        slot.addEventListener('mouseup',recomputeZ);
        slot.addEventListener('touchend',recomputeZ,{passive:true});
        slot.addEventListener('click',recomputeZ);
        setBadge(t('debt_actions.common.magnitude_hint', 'A Hint'));
      } catch(e) {
        console.error('Z$ load failed:', e);
        zWrap.innerHTML = `<p style="color:#ff3333;">${t('debt_actions.predatory_lend.no_zollars', 'No Z$ available.')}</p>`;
        setBadge(t('debt_actions.common.magnitude_none', 'None'));
      }
  
      const toCapital=()=>{
        sw.setActive(sw.cap);
        zWrap.style.display='none';
        capWrap.style.display='';
        const amt = parseFloat(contentDiv.querySelector('.cds-capital')?.value)||0;
        const v = Math.min(1, affordabilityProxy01(amt)*1.15); // CDS perceived inflation
        setBadge(perceivedLabelFrom01(v));
      };
      const toZollars=()=>{
        sw.setActive(sw.zol);
        capWrap.style.display='none';
        zWrap.style.display='';
        const total = zWrap._ZCOMP ? zWrap._ZCOMP.total() : 0;
        const frac = zWrap._ZTOTAL>0 ? total/zWrap._ZTOTAL : 0;
        setBadge(perceivedLabelFrom01(Math.min(1, frac*1.15)));
      };
      sw.cap.onclick = toCapital;
      sw.zol.onclick = toZollars;
  
      // live update for capital
      contentDiv.addEventListener('input', (e)=>{
        if (e.target && e.target.classList.contains('cds-capital')) {
          const amt = parseFloat(e.target.value)||0;
          setBadge(perceivedLabelFrom01(Math.min(1, affordabilityProxy01(amt)*1.15)));
        }
      });
  
      modalManager.createModal(t('debt_actions.predatory_lend.modal_title', 'Z-Payday CDO (Predatory Loan)'), contentDiv, async (modal)=>{
        if (!isMarketListing) {
          // Target specific victim
          const victim = modalManager.getSelectedPortfolio(modal);
          if (!victim || !victim.value) return modalManager.showMessage(t('debt_actions.predatory_lend.error_no_victim', 'Select a victim.'), false, 3000, true);

          let payload;
          if (capWrap.style.display === '') {
            const amount = parseFloat(contentDiv.querySelector('.cds-capital').value)||0;
            if (!amount || amount<=0) return modalManager.showMessage(t('debt_actions.predatory_lend.error_invalid_amount', 'Enter a valid amount.'), false, 3000, true);
            payload = { exploiterId: portfolioId, victimId: victim.value, amount, sessionId, package: { points: amount, zollars: 0, stocks: 0 } };
          } else {
            if (!zWrap._ZCOMP) return modalManager.showMessage(t('debt_actions.predatory_lend.error_no_zollars_selected', 'No Z$ selected.'), false, 3000, true);
            const zBid = zWrap._ZCOMP.getBid(), totalZ = zWrap._ZCOMP.total();
            if (totalZ<=0) return modalManager.showMessage(t('debt_actions.predatory_lend.error_select_zollars', 'Select some Z$.'), false, 3000, true);
            payload = { exploiterId: portfolioId, victimId: victim.value, amount: totalZ, sessionId, package:{ points:0, zollars:zBid, stocks:0 } };
          }

          modalManager.closeModal();
          await fetch(`${apiBaseUrl}/api/social-economy/cds`, {
            method:'POST', headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
            body: JSON.stringify(payload)
          });
          modalManager.showMessage(t('debt_actions.predatory_lend.success_targeted', 'Z-Payday CDO offer sent to victim. Reloads every 60 seconds once accepted.'), true);
        } else {
          // List on market
          let payload;
          if (capWrap.style.display === '') {
            const amount = parseFloat(contentDiv.querySelector('.cds-capital').value)||0;
            if (!amount || amount<=0) return modalManager.showMessage(t('debt_actions.predatory_lend.error_invalid_amount', 'Enter a valid amount.'), false, 3000, true);
            payload = {
              exploiterId: portfolioId, amount, sessionId,
              package: { points: amount, zollars: 0, stocks: 0 },
              principalType: 'capital'
            };
          } else {
            if (!zWrap._ZCOMP) return modalManager.showMessage(t('debt_actions.predatory_lend.error_no_zollars_selected', 'No Z$ selected.'), false, 3000, true);
            const zBid = zWrap._ZCOMP.getBid(), totalZ = zWrap._ZCOMP.total();
            if (totalZ<=0) return modalManager.showMessage(t('debt_actions.predatory_lend.error_select_zollars', 'Select some Z$.'), false, 3000, true);
            payload = {
              exploiterId: portfolioId, amount: totalZ, sessionId,
              package:{ points:0, zollars:zBid, stocks:0 },
              principalType: 'zollars',
              zollarBreakdown: zBid
            };
          }

          modalManager.closeModal();
          await fetch(`${apiBaseUrl}/api/social-economy/market/list-cdo`, {
            method:'POST', headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
            body: JSON.stringify(payload)
          });
          modalManager.showMessage(t('debt_actions.predatory_lend.success_market', 'Z-Payday CDO listed on market. Disguised as attractive offer.'), true);
        }
        if (window.emotionalMeter) window.emotionalMeter.fetchData();
      });
    });
  }



// === Borrow (Z$ default, toggle to Capital) - Borrower initiates M Note ===
  const borrowCapitalButton = document.getElementById('borrowCapitalButton');
  if (borrowCapitalButton) {
    borrowCapitalButton.addEventListener('click', async () => {
      const portfolioId = localStorage.getItem('portfolioId');
      const sessionId   = localStorage.getItem('sessionId');
      const token       = localStorage.getItem('token');
      if (!portfolioId || !sessionId || !token)
        return modalManager.showMessage(t('debt_actions.common.error_login_required', 'Please log in first'), false);
  
      const res = await fetch(`${apiBaseUrl}/api/portfolios/active`, {
        method:'POST',
        headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
        body: JSON.stringify({ sessionId })
      });
      const data = await res.json();
      const portfolios = Array.isArray(data) ? data : (data.portfolios||[]);
  
      const humanInterestMap = { Amazing:0.08, Great:0.12, Good:0.16, Neutral:0.26, Bad:0.35, Horrible:0.45, Catastrophic:0.60 };

      const contentDiv = document.createElement('div');
      contentDiv.innerHTML = `<p style="font-family:'bithigh',monospace;color:#fcba03;"><strong>${t('debt_actions.borrow.intro', 'Request a loan (M Note).')}</strong> ${t('debt_actions.borrow.vulnerability_required', 'Disclose your emotional debt. Vulnerability.')}</p>`;

      // M Note: Vulnerability disclosure (required) - FIRST
      const vulnDiv = document.createElement('div');
      vulnDiv.style.cssText = 'margin-top:12px;';
      vulnDiv.innerHTML = `
        <label style="display:block;margin-bottom:4px;font-family:'bithigh',monospace;color:#fcba03;font-size:1.05em;font-weight:bold;">
          ${t('debt_actions.borrow.vulnerability_label', 'Why do you need this loan? (Emotional debt disclosure - min 20 chars)')}
        </label>
        <textarea class="vuln-reason" rows="3" placeholder="${t('debt_actions.borrow.vulnerability_placeholder', 'Disclose your emotional debt / vulnerability here...')}" style="
          width:100%;padding:8px;font-family:'bithigh',monospace;background:rgba(0,0,0,.3);
          border:1px solid #fcba03;border-radius:6px;color:#fcba03;resize:vertical;
        "></textarea>
        <div style="display:flex;justify-content:space-between;margin-top:4px;">
          <span class="vuln-char-count" style="font-family:'bithigh',monospace;font-size:0.85em;color:#999;">${t('debt_actions.borrow.char_count', '0 / 20 chars')}</span>
          <span class="vuln-severity-label" style="font-family:'bithigh',monospace;font-size:0.85em;color:#fcba03;">${t('debt_actions.borrow.severity_label', 'Severity')}: ${t('debt_actions.common.magnitude_hint', 'A Hint')}</span>
        </div>
        <label style="display:block;margin-top:6px;font-family:'bithigh',monospace;font-size:0.9em;color:#fcba03;">
          ${t('debt_actions.borrow.severity_question', 'How severe is your need?')}
        </label>
        <input type="range" class="vuln-severity" min="0" max="100" value="35" style="width:100%;margin-top:4px;">
      `;
      contentDiv.appendChild(vulnDiv);

      const badgeRow = document.createElement('div');
      badgeRow.style.cssText='display:flex;justify-content:flex-end;margin:16px 0 4px;';
      badgeRow.innerHTML = `<span id="lend-perceived" style="
        font-family:'bithigh',monospace;color:#fcba03;border:1px solid #fcba03;
        padding:6px 10px;border-radius:8px;background:rgba(0,0,0,.35);
        box-shadow:0 0 6px rgba(252,186,3,.4) inset;">${t('debt_actions.common.perceived_magnitude', 'Perceived magnitude')}: ${t('debt_actions.common.magnitude_none', 'None')}</span>`;
      contentDiv.appendChild(badgeRow);
      const setBadge = (txt)=> contentDiv.querySelector('#lend-perceived').textContent = `${t('debt_actions.common.perceived_magnitude', 'Perceived magnitude')}: ${txt}`;

      // Abstract sliders section
      const sliderWrap = document.createElement('div');
      sliderWrap.id = 'abstract-loan-sliders';
      sliderWrap.innerHTML = `<p style="margin:12px 0 4px;font-family:'bithigh',monospace;color:#999;font-size:0.9em;">${t('debt_actions.borrow.move_sliders', 'Move sliders to express your need:')}</p>`;
      contentDiv.appendChild(sliderWrap);

      // Add "Target Lender" vs "List on Market" choice
      const targetChoice = document.createElement('div');
      targetChoice.style.cssText = 'margin:12px 0;display:flex;gap:10px;';
      targetChoice.innerHTML = `
        <button class="mode-btn active" id="targetLenderBtn" style="flex:1;padding:10px;background:rgba(252,186,3,.25);border:1px solid #fcba03;color:#fff;font-family:'bithigh',monospace;cursor:pointer;border-radius:8px;">Target Specific Lender</button>
        <button class="mode-btn" id="listOnMarketBtn" style="flex:1;padding:10px;background:rgba(0,0,0,.35);border:1px solid rgba(255,255,255,.3);color:#fcba03;font-family:'bithigh',monospace;cursor:pointer;border-radius:8px;">List on Market</button>
      `;
      contentDiv.appendChild(targetChoice);

      // Lender selection (only shown when targeting specific lender)
      const lenderSelectContainer = document.createElement('div');
      lenderSelectContainer.id = 'lenderSelectContainer';
      const lenderSelect = modalManager.createPortfolioSelection(portfolios, t('debt_actions.borrow.lender_label', 'Choose Lender:'));
      lenderSelectContainer.appendChild(lenderSelect);
      contentDiv.appendChild(lenderSelectContainer);

      // Toggle between target lender and list on market
      let isMarketListing = false;
      const targetLenderBtn = targetChoice.querySelector('#targetLenderBtn');
      const listOnMarketBtn = targetChoice.querySelector('#listOnMarketBtn');

      const showLenderSelect = () => {
        isMarketListing = false;
        lenderSelectContainer.style.display = '';
        targetLenderBtn.style.background = 'rgba(252,186,3,.25)';
        targetLenderBtn.style.color = '#fff';
        listOnMarketBtn.style.background = 'rgba(0,0,0,.35)';
        listOnMarketBtn.style.color = '#fcba03';
      };

      const showMarketListing = () => {
        isMarketListing = true;
        lenderSelectContainer.style.display = 'none';
        listOnMarketBtn.style.background = 'rgba(252,186,3,.25)';
        listOnMarketBtn.style.color = '#fff';
        targetLenderBtn.style.background = 'rgba(0,0,0,.35)';
        targetLenderBtn.style.color = '#fcba03';
      };

      targetLenderBtn.onclick = showLenderSelect;
      listOnMarketBtn.onclick = showMarketListing;

      // Update char count and severity label
      const vulnTextarea = vulnDiv.querySelector('.vuln-reason');
      const charCount = vulnDiv.querySelector('.vuln-char-count');
      const severitySlider = vulnDiv.querySelector('.vuln-severity');
      const severityLabel = vulnDiv.querySelector('.vuln-severity-label');

      vulnTextarea.addEventListener('input', () => {
        const len = vulnTextarea.value.length;
        charCount.textContent = `${len} ${t('debt_actions.borrow.char_suffix', '/ 20 chars')}`;
        charCount.style.color = len >= 20 ? '#3dd202' : '#ff3333';
      });

      severitySlider.addEventListener('input', () => {
        const severity01 = Number(severitySlider.value) / 100;
        severityLabel.textContent = `${t('debt_actions.borrow.severity_label', 'Severity')}: ${perceivedLabelFrom01(severity01)}`;
      });

      const clamp01 = v => Math.max(0, Math.min(1, v));
      const perceivedLabelFrom01 = v => qualitativeFromFraction_LOCAL(clamp01(v));
      const affordabilityProxy01 = (amount, wealthNow=Number(localStorage.getItem('tradingPoints')||0), near=0)=>{
        const z = Number(wealthNow)||0, need=Number(near)||0; const buffer=Math.max(1,z-need);
        return clamp01(Math.log1p(Number(amount)||0) / Math.log1p(buffer+(Number(amount)||0)));
      };
  
      // Mount abstract sliders
      const sliderComp = createAbstractBorrowSliders_LOCAL();
      sliderWrap.appendChild(sliderComp.el);

      // Update badge when sliders change
      const updateBadge = () => {
        const zMag = sliderComp.getZollarMagnitude();
        const cMag = sliderComp.getCapitalMagnitude();
        const maxMag = Math.max(zMag, cMag);
        const frac = maxMag / 100;
        setBadge(perceivedLabelFrom01(frac));
      };

      sliderWrap.addEventListener('input', updateBadge);
      setBadge(t('debt_actions.common.magnitude_none', 'None'));

      modalManager.createModal(t('debt_actions.borrow.modal_title', 'M Note (Request Loan)'), contentDiv, async (modal)=>{
        // Validate vulnerability disclosure
        const vulnerabilityReason = vulnTextarea.value.trim();
        if (vulnerabilityReason.length < 20) {
          return modalManager.showMessage(t('debt_actions.borrow.error_vulnerability_required', 'M Note requires vulnerability disclosure (min 20 characters).'), false, 3000, true);
        }

        const vulnerabilityScore = Number(severitySlider.value) / 100;

        // Get abstract slider values
        const zollarMag = sliderComp.getZollarMagnitude();
        const capitalMag = sliderComp.getCapitalMagnitude();

        if (!sliderComp.hasSelection()) {
          return modalManager.showMessage(t('debt_actions.borrow.error_move_slider', 'Please move at least one slider to express your need.'), false, 3000, true);
        }

        // Determine principal type and calculate actual amounts
        let principalType, packageData, totalAmount;

        if (zollarMag > capitalMag) {
          // Zollar-based loan
          principalType = 'zollars';
          const zollarCount = Math.max(1, Math.floor(zollarMag / 10)); // 0-100 → 1-10 zollars

          // Create a generic zollar breakdown (will use borrower's actual zollars)
          // Backend will handle the actual transfer from borrower's holdings
          packageData = {
            points: 0,
            zollars: {
              _abstract: true,
              _count: zollarCount,
              _magnitude: zollarMag
            },
            stocks: 0
          };
          totalAmount = zollarCount * 50; // Estimate value
        } else {
          // Capital-based loan
          principalType = 'capital';
          const capitalAmount = Math.max(1, Math.floor(capitalMag * 10)); // 0-100 → 1-1000 capital
          packageData = {
            points: capitalAmount,
            zollars: 0,
            stocks: 0
          };
          totalAmount = capitalAmount;
        }

        // Emotional interest scales with perceived need
        const needIntensity = Math.max(zollarMag, capitalMag) / 100;
        const baseRate = 0.16 + (needIntensity * 0.24); // 16% to 40% based on need

        // Check if targeting lender or listing on market
        if (!isMarketListing) {
          // Target specific lender
          const lender = modalManager.getSelectedPortfolio(modal);
          if (!lender || !lender.value) return modalManager.showMessage(t('debt_actions.borrow.error_no_lender', 'Please select a lender.'), false, 3000, true);

          const payload = {
            borrowerId: portfolioId,
            lenderId: lender.value,
            amount: totalAmount,
            principalType,
            humanInterest: baseRate,
            package: packageData,
            sessionId,
            type: 'loan',
            vulnerabilityReason,
            vulnerabilityScore,
            abstractRequest: { zollarMagnitude: zollarMag, capitalMagnitude: capitalMag }
          };

          modalManager.closeModal();
          await fetch(`${apiBaseUrl}/api/social-economy/lend`, {
            method:'POST',
            headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
            body: JSON.stringify(payload)
          });
          modalManager.showMessage(t('debt_actions.borrow.success_targeted', 'M Note loan request sent to lender.'), true);
        } else {
          // List on market
          const payload = {
            borrowerId: portfolioId,
            amount: totalAmount,
            principalType,
            humanInterest: baseRate,
            package: packageData,
            vulnerabilityReason,
            vulnerabilityScore,
            sessionId,
            abstractRequest: { zollarMagnitude: zollarMag, capitalMagnitude: capitalMag }
          };

          modalManager.closeModal();
          await fetch(`${apiBaseUrl}/api/social-economy/market/list-mnote`, {
            method:'POST',
            headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
            body: JSON.stringify(payload)
          });
          modalManager.showMessage(t('debt_actions.borrow.success_market', 'M Note listed on market for lenders to purchase.'), true);
        }
        if (window.emotionalMeter) window.emotionalMeter.fetchData();
      });
    });
  }


// === Request Aid (V Note) - Target specific user with vulnerability request ===
  const requestHandoutButton = document.getElementById('requestHandoutButton');
  if (requestHandoutButton) {
    requestHandoutButton.addEventListener('click', async () => {
      const portfolioId = localStorage.getItem('portfolioId');
      const sessionId   = localStorage.getItem('sessionId');
      const token       = localStorage.getItem('token');
      if (!portfolioId || !sessionId || !token) {
        return modalManager.showMessage(t('debt_actions.common.error_login_required', 'Please log in first'), false);
      }

      // Fetch active portfolios
      const res = await fetch(`${apiBaseUrl}/api/portfolios/active`, {
        method:'POST',
        headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
        body: JSON.stringify({ sessionId })
      });
      const data = await res.json();
      const portfolios = Array.isArray(data) ? data : (data.portfolios||[]);

      const clamp01 = v => Math.max(0, Math.min(1, v));
      const perceivedLabelFrom01 = v => qualitativeFromFraction_LOCAL(clamp01(v));

      const contentDiv = document.createElement('div');
      contentDiv.innerHTML = `
        <p><strong>${t('debt_actions.request_aid.intro', 'Request aid (V Note).')}</strong> ${t('debt_actions.request_aid.description', 'Ask someone for a handout by sharing your vulnerability.')}</p>
        <p style="font-size:0.9em;color:#fcba03;">${t('debt_actions.request_aid.accept_decline_note', 'They can accept or decline your request.')}</p>
      `;

      // Vulnerability disclosure FIRST - most important
      const justDiv = document.createElement('div');
      justDiv.style.cssText = 'margin-top:12px;';
      justDiv.innerHTML = `
        <label style="font-family:'bithigh',monospace;color:#fcba03;font-size:1.05em;font-weight:bold;">${t('debt_actions.request_aid.vulnerability_label', 'Explain why you need aid (vulnerability disclosure - min 20 chars)')}</label>
        <textarea class="handout-just" rows="4" placeholder="${t('debt_actions.request_aid.vulnerability_placeholder', 'Share your vulnerability...')}" style="
          width:100%;padding:8px;font-family:'bithigh',monospace;background:rgba(0,0,0,.3);
          border:1px solid #fcba03;border-radius:6px;color:#fcba03;resize:vertical;
        "></textarea>
        <div style="margin-top:10px;">
          <label style="font-family:'bithigh',monospace;color:#fcba03;">${t('debt_actions.request_aid.severity_question', 'Situation severity (how urgent is this)')}</label>
          <input type="range" class="handout-severity" min="0" max="100" value="35" style="width:100%;">
          <div style="display:flex;justify-content:flex-end;">
            <span id="handout-perceived-label" style="
              margin-top:6px;font-family:'bithigh',monospace;color:#fcba03;border:1px solid #fcba03;
              padding:6px 10px;border-radius:8px;background:rgba(0,0,0,.35);
              box-shadow:0 0 6px rgba(252,186,3,.4) inset;">${t('debt_actions.common.perceived_magnitude', 'Perceived magnitude')}: ${t('debt_actions.common.magnitude_hint', 'A Hint')}</span>
          </div>
        </div>
      `;
      contentDiv.appendChild(justDiv);

      // Target selection - comes after vulnerability disclosure
      const targetSelect = modalManager.createPortfolioSelection(portfolios, t('debt_actions.request_aid.target_label', 'Request aid from:'));
      contentDiv.appendChild(targetSelect);
  
      const sev = contentDiv.querySelector('.handout-severity');
      const badge = contentDiv.querySelector('#handout-perceived-label');
      const recompute = ()=> badge.textContent = `${t('debt_actions.common.perceived_magnitude', 'Perceived magnitude')}: ${perceivedLabelFrom01((Number(sev.value)||0)/100)}`;
      setTimeout(recompute,0);
      contentDiv.addEventListener('input', e => { if (e.target===sev) recompute(); });

      modalManager.createModal(t('debt_actions.request_aid.modal_title', 'V Note (Request Aid)'), contentDiv, async (modal)=>{
        // Validate target
        const target = modalManager.getSelectedPortfolio(modal);
        if (!target || !target.value) return modalManager.showMessage(t('debt_actions.request_aid.error_no_target', 'Please select who to request aid from.'), false, 3000, true);

        // Validate vulnerability disclosure
        const justification = contentDiv.querySelector('.handout-just').value.trim();
        if (justification.length < 20) {
          return modalManager.showMessage(t('debt_actions.request_aid.error_vulnerability_required', 'V Note requires vulnerability disclosure (min 20 characters).'), false, 3000, true);
        }
        const severity01 = (Number(sev.value)||0)/100;

        modalManager.closeModal();
        const response = await fetch(`${apiBaseUrl}/api/social-economy/vnote`, {
          method:'POST',
          headers:{ Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
          body: JSON.stringify({
            requesterId: portfolioId,
            targetId: target.value,
            justification,
            vulnerabilityScore: severity01,
            sessionId
          })
        });
        const result = await response.json();
        modalManager.showMessage(result.message || t('debt_actions.request_aid.success', 'V Note request sent. Awaiting response.'), result.success !== false);
        if (window.emotionalMeter) window.emotionalMeter.fetchData();
      });
    });
  }

});
function handleCDSVictimCollapse({ title, message }) {
  if (typeof showPopup === 'function') showPopup(title, message, { theme:'danger' });
  if (typeof showNotification === 'function') showNotification('You lost assets in a CDS collapse.', false);
  if (window.emotionalMeter) window.emotionalMeter.fetchData();
}
function handleCDSExploiterGuilt({ title, message }) {
  if (typeof showPopup === 'function') showPopup(title, message, { theme:'warning' });
  setTimeout(()=>{ if (typeof showNotification === 'function') showNotification('You profited, but your victim has fallen into debt.', true); }, 12000);
  if (window.emotionalMeter) window.emotionalMeter.fetchData();
}
function handleCDSInitiation({ title, message }) {
  if (typeof showNotification === 'function') showNotification(message || 'Predatory loan initiated.', true);
}

// expose to global (optional)
window.modalManager = modalManager;
window.handleCDSVictimCollapse = handleCDSVictimCollapse;
window.handleCDSExploiterGuilt = handleCDSExploiterGuilt;
window.handleCDSInitiation = handleCDSInitiation;

// named exports for Parcel imports
export { qualitativeFromFraction_LOCAL , modalManager, handleCDSVictimCollapse, handleCDSExploiterGuilt, handleCDSInitiation };
